import PopUp from '@/common/components/PopUp';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import { useTutorialStages } from './hooks/useTutorialStages';
import {
	Wrapper,
	Title,
	HorizontalList,
	Stage,
	StageImage,
	StageItem,
	StageItemIcon,
	StageItemText,
	StageItems,
	SkipButton,
	ProgressIndicatorList,
	ProgressIndicator,
} from './SceneTutorial.styles';
import GradientButton from '@/common/components/custom/buttons/GradientButton';
import { useRef, useState, useEffect, useMemo } from 'react';

type Props = { onClose: () => void };

function SceneTutorial({ onClose }: Props) {
	const langData = useLanguageData('features.sceneV3');
	const config = useTutorialStages();

	const [currentStage, setCurrentStage] = useState(0);
	const stageElementRef = useRef<HTMLDivElement>(null);
	const stagesListRef = useRef<HTMLDivElement>(null);

	const stageWidth = () => (stageElementRef.current?.offsetWidth || 0) + 30;

	const stagesScrollHandler = (event: Event) => {
		const target = event.target as HTMLDivElement;
		setCurrentStage(Math.round(target.scrollLeft / (stageWidth() + 30)));
	};

	const scrollToNextStage = () => {
		const scrollValue = (currentStage + 1) * stageWidth();

		stagesListRef.current?.scrollTo({
			left: scrollValue,
			behavior: 'smooth',
		});
	};

	useEffect(() => {
		const stagesList = stagesListRef.current;
		if (!stagesList) return;

		stagesList.addEventListener('scroll', stagesScrollHandler);

		return () => {
			stagesList.removeEventListener('scroll', stagesScrollHandler);
		};
	}, []);

	return (
		<PopUp setClose={onClose}>
			<Wrapper>
				<Title>{langData.howToTalkInScenes}</Title>
				<HorizontalList ref={stagesListRef}>
					{config.map((stage) => (
						<Stage ref={stageElementRef} key={stage.image}>
							<StageImage src={stage.image} />
							<StageItems>
								{stage.items.map((item) => (
									<StageItem key={item.text}>
										<StageItemIcon src={item.icon} />
										<StageItemText>{item.text}</StageItemText>
									</StageItem>
								))}
							</StageItems>
						</Stage>
					))}
				</HorizontalList>
				<ProgressIndicatorList>
					{Array.from({ length: config.length }, (_, index) => (
						<ProgressIndicator isActive={index === currentStage} />
					))}
				</ProgressIndicatorList>
				<SkipButton onClick={onClose}>{langData.skip}</SkipButton>
				<GradientButton
					title={currentStage === 4 ? langData.start : langData.next}
					onClick={currentStage === 4 ? onClose : scrollToNextStage}
					style={{ width: 180, aspectRatio: 4.5, fontSize: 16 }}
				/>
			</Wrapper>
		</PopUp>
	);
}

export default SceneTutorial;
